/**
 * Danger Zone card for the settings pages — the one place on a customer,
 * site group or user page where the record is taken out of service or
 * brought back. It has to read as a warning before a word of it is read:
 * red border, red icon and title, so the rows beneath are never mistaken
 * for ordinary settings. Every settings page mounts it directly beneath its
 * first section so it is always found in the same spot.
 *
 * Contains:
 * - SettingsDangerZone (default export) — the card; children are
 *   SettingsFieldRow rows whose control is the action button
 * - DANGER_ZONE_CONTROL_WIDTH — right-column budget shared by those rows so
 *   the action buttons line up from page to page
 */

import WarningAmberIcon from '@mui/icons-material/WarningAmberOutlined';
import { Box, Divider, Stack, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

export const DANGER_ZONE_CONTROL_WIDTH = 220;

const ICON_SIZE = 40;

/**
 * @param {object} props
 * @param {string} [props.id] — scroll anchor; one danger zone per page
 * @param {React.ReactNode} [props.description] — what this page's zone can do
 * @param {React.ReactNode} [props.chip] — optional gate marker beside the title
 * @param {React.ReactNode} props.children — SettingsFieldRow rows
 * @param {object} [props.sx]
 */
export default function SettingsDangerZone({ id = 'settings-danger-zone', description, chip, children, sx }) {
  const { t } = useTranslation();
  return (
    <Box
      id={id}
      sx={{
        backgroundColor: 'background.paper',
        borderRadius: 'var(--card-radius)',
        border: 1,
        borderColor: 'error.main',
        px: { xs: 2, md: 3 },
        py: 2.5,
        scrollMarginTop: 16,
        ...sx,
      }}
    >
      <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0 }}>
        <Box
          sx={{
            width: ICON_SIZE,
            height: ICON_SIZE,
            borderRadius: 'var(--card-icon-radius)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'error.light',
            color: 'error.main',
            flexShrink: 0,
            '& svg': { fontSize: 22 },
          }}
        >
          <WarningAmberIcon />
        </Box>
        <Stack spacing={0.5} sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="h6" component="h2" color="error.main" sx={{ lineHeight: 1.3 }}>
              {t('settings.dangerZone', 'Danger Zone')}
            </Typography>
            {chip}
          </Stack>
          {description ? (
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          ) : null}
        </Stack>
      </Stack>
      <Divider sx={{ my: 2.5 }} />
      <Box
        sx={{
          borderRadius: 'var(--card-radius)',
          border: 1,
          borderColor: 'error.main',
          overflow: 'hidden',
          px: { xs: 1.5, sm: 2 },
          py: 0.5,
        }}
      >
        {children}
      </Box>
    </Box>
  );
}
